import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode
} from 'react';

// --- Interfaces ---

export interface CreatorInsight {
  id: string;
  creator_profile_id: string;
  status: 'shared' | 'closed' | 'scheduled' | 'open';
  title: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

// --- Context Shape ---

interface DashboardContextType {
  // Core state
  brandId: string | null;
  selectedInsight: CreatorInsight | null;
  selectedCreatorId: string | null;

  // Actions
  setBrandId: (brandId: string) => void;
  selectInsight: (insight: CreatorInsight | null) => void;
  selectCreator: (creatorId: string) => void;
  updateInsightStatus: (insightId: string, status: CreatorInsight['status']) => void;
}

// --- Context Definition ---

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

// --- Custom Hook ---

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
};

// --- Provider ---

interface DashboardProviderProps {
  children: ReactNode;
  initialBrandId?: string;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
                                                                      children,
                                                                      initialBrandId = null
                                                                    }) => {
  // Core state
  const [brandId, setBrandIdState] = useState<string | null>(initialBrandId);
  const [selectedInsight, setSelectedInsight] = useState<CreatorInsight | null>(null);
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(null);

  // Actions
  const setBrandId = useCallback((newBrandId: string) => {
    console.log("[DashboardContext] Brand changed:", newBrandId);
    setBrandIdState(newBrandId);
    // Clear selections when brand changes
    setSelectedInsight(null);
    setSelectedCreatorId(null);
  }, []);

  const selectInsight = useCallback((insight: CreatorInsight | null) => {
    console.log("[DashboardContext] Insight selected:", insight?.id);
    setSelectedInsight(insight);
    // Auto-select the creator when insight is selected
    if (insight) {
      setSelectedCreatorId(insight.creator_profile_id);
    }
  }, []);

  const selectCreator = useCallback((creatorId: string) => {
    console.log("[DashboardContext] Creator selected:", creatorId);
    setSelectedCreatorId(creatorId);
    // Clear insight selection if it doesn't belong to this creator
    setSelectedInsight(prev =>
      prev && prev.creator_profile_id !== creatorId ? null : prev
    );
  }, []);

  const updateInsightStatus = useCallback((insightId: string, status: CreatorInsight['status']) => {
    console.log("[DashboardContext] Insight status updated:", insightId, status);

    // Update the selected insight if it matches
    setSelectedInsight(prev =>
      prev?.id === insightId
        ? { ...prev, status, updated_at: new Date().toISOString() }
        : prev
    );

    // Here you would typically also update your insights list/cache
    // or trigger a refetch depending on your data management strategy
  }, []);

  const value = useMemo(() => ({
    brandId,
    selectedInsight,
    selectedCreatorId,
    setBrandId,
    selectInsight,
    selectCreator,
    updateInsightStatus,
  }), [
    brandId,
    selectedInsight,
    selectedCreatorId,
    setBrandId,
    selectInsight,
    selectCreator,
    updateInsightStatus,
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};